/* Modal de encerramento de plantão — gera passagem via IA + edit + copiar */
import React, { useState, useCallback, useEffect, forwardRef } from 'react';
import { gerarPassagemPlantao } from '../api/plantao';

type EstadoModal = 'confirmar' | 'gerando' | 'editar' | 'erro';

interface PlantaoCloseModalProps {
  /** ID da sessão de chat atual (para o backend buscar a memória) */
  sessionId: string;
  /** ID do plantão ativo (do usePlantao) */
  plantaoId?: string | null;
  /** Timestamp de início do plantão (do usePlantao) */
  inicio?: number | null;
  /** Casos atendidos no plantão (do usePlantao) */
  casos?: import('../api/plantao').CasoPlantao[];
  /** Consultas IA realizadas no plantão */
  consultasIa?: number;
  /** Créditos IA gastos no plantão */
  creditosUsados?: number;
  /** Callback após fechar definitivamente o plantão */
  onConfirmFechar: () => void;
  /** Função de tradução */
  t: (key: string, vars?: Record<string, string | number>) => string;
}

/** Copia texto via clipboard com fallback execCommand (padrão do projeto) */
function copiarTexto(text: string): boolean {
  try {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      return true;
    }
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Modal de encerramento de plantão.
 * Fluxo:
 * 1. Confirmar: "Gerar passagem?" → [SIM] / [NÃO, SÓ FECHAR]
 * 2. Gerando: loading enquanto IA gera texto
 * 3. Editar: textarea com passagem gerada + [COPIAR] [FECHAR PLANTÃO]
 * 4. Erro: mensagem de erro + botão tentar novamente
 */
export const PlantaoCloseModal = forwardRef<HTMLDivElement, PlantaoCloseModalProps>(
  ({ sessionId, plantaoId, inicio, casos, consultasIa, creditosUsados, onConfirmFechar, t }, ref) => {
    const [estado, setEstado] = useState<EstadoModal>('confirmar');
    const [passagem, setPassagem] = useState('');
    const [erroMsg, setErroMsg] = useState('');
    const [copiado, setCopiado] = useState(false);

    // Fecha com Escape apenas na tela de confirmação
    useEffect(() => {
      function handleKey(e: KeyboardEvent) {
        if (e.key === 'Escape' && estado === 'confirmar') {
          onConfirmFechar();
        }
      }
      document.addEventListener('keydown', handleKey);
      return () => document.removeEventListener('keydown', handleKey);
    }, [estado, onConfirmFechar]);

    /** Gera passagem via backend */
    const handleGerarPassagem = useCallback(async () => {
      setEstado('gerando');
      setErroMsg('');
      try {
        const texto = await gerarPassagemPlantao({
          sessionId,
          plantaoId,
          inicio,
          casos,
          consultasIa,
          creditosUsados,
        });
        setPassagem(texto);
        setEstado('editar');
      } catch (err) {
        setErroMsg(err instanceof Error ? err.message : t('errors.generic'));
        setEstado('erro');
      }
    }, [sessionId, plantaoId, inicio, casos, consultasIa, creditosUsados, t]);

    /** Copia passagem e mostra feedback */
    const handleCopiar = useCallback(() => {
      const ok = copiarTexto(passagem);
      if (ok) {
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
      }
    }, [passagem]);

    /** Fecha definitivamente — limpa plantão */
    const handleFecharDefinitivo = useCallback(() => {
      onConfirmFechar();
    }, [onConfirmFechar]);

    /* Estilos compartilhados */
    const btnPrimario: React.CSSProperties = {
      fontFamily: 'var(--font-sans)',
      fontSize: '12px',
      fontWeight: 600,
      padding: '11px 20px',
      background: 'var(--cta-blue)',
      border: '1px solid var(--cta-blue)',
      color: '#FFFFFF',
      cursor: 'pointer',
      letterSpacing: '0.02em',
      boxShadow: 'none',
      transition: 'box-shadow 180ms ease, border-color 180ms ease',
    };

    const btnSecundario: React.CSSProperties = {
      fontFamily: 'var(--font-sans)',
      fontSize: '12px',
      fontWeight: 600,
      padding: '11px 20px',
      background: 'transparent',
      border: '1px solid rgba(124,124,248,0.40)',
      color: 'var(--ink-light)',
      cursor: 'pointer',
      letterSpacing: '0.02em',
      transition: 'box-shadow 180ms ease, border-color 180ms ease, color 180ms ease',
    };

    return (
      /* Backdrop */
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('plantao.close.title')}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(34, 37, 50, 0.92)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 700,
          padding: '20px',
        }}
      >
        {/* Card */}
        <div
          ref={ref}
          style={{
            maxWidth: estado === 'editar' ? '640px' : '420px',
            width: '100%',
            background: 'var(--ink-soft)',
            border: '1px solid var(--violet)',
            boxShadow: '0 0 24px rgba(124,124,248,0.18)',
            padding: '32px',
          }}
        >
          {/* Título */}
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '14px',
              fontWeight: 700,
              color: '#FFFFFF',
              textShadow: 'none',
              letterSpacing: '0.04em',
              marginBottom: '18px',
            }}
          >
            {t('plantao.close.title')}
          </div>

          {/* Estado: confirmar */}
          {estado === 'confirmar' && (
            <>
              <p
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: '14px',
                  color: 'var(--ink-light)',
                  lineHeight: 1.7,
                  marginBottom: '28px',
                }}
              >
                {t('plantao.close.confirm')}
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={handleFecharDefinitivo} style={btnSecundario}>
                  {t('plantao.close.skipPassage')}
                </button>
                <button type="button" onClick={handleGerarPassagem} style={btnPrimario}>
                  {t('plantao.close.generatePassage')}
                </button>
              </div>
            </>
          )}

          {/* Estado: gerando */}
          {estado === 'gerando' && (
            <div
              style={{
                textAlign: 'center',
                padding: '20px 0',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: '13px',
                  color: 'var(--violet)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <span style={{ animation: 'cursor-blink 0.6s step-end infinite' }}>●</span>
                {t('plantao.close.generating')}
              </div>
            </div>
          )}

          {/* Estado: editar passagem */}
          {estado === 'editar' && (
            <>
              <textarea
                value={passagem}
                onChange={(e) => setPassagem(e.target.value)}
                rows={16}
                aria-label="Passagem de plantão"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'var(--ink)',
                  border: '1px solid var(--ink-mute)',
                  color: 'var(--ink-light)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: '13px',
                  lineHeight: 1.7,
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                  marginBottom: '20px',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(124,124,248,0.50)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--ink-mute)')}
              />
              {/* 20/05/2026: botão COPIAR igual ao da extensão — coral idle,
                  vira green-fluor com glow forte quando copiado. */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
                <button
                  type="button"
                  onClick={handleCopiar}
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: '11px 20px',
                    background: copiado ? 'var(--green-fluor)' : 'transparent',
                    border: `1px solid ${copiado ? 'var(--green-fluor)' : 'rgba(124,124,248,0.40)'}`,
                    color: copiado ? 'var(--ink)' : 'var(--ink-light)',
                    cursor: 'pointer',
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    boxShadow: copiado
                      ? '0 0 12px rgba(0,255,65,0.7), 0 0 28px rgba(0,255,65,0.35)'
                      : 'none',
                    transition: 'background 180ms ease, border-color 180ms ease, box-shadow 180ms ease, color 180ms ease',
                  }}
                >
                  {copiado
                    ? `✓ ${t('plantao.close.copied', { defaultValue: 'COPIADO' })}`
                    : t('plantao.close.copyPassage')}
                </button>
                <button type="button" onClick={handleFecharDefinitivo} style={btnPrimario}>
                  {t('plantao.close.finalClose')}
                </button>
              </div>
            </>
          )}

          {/* Estado: erro */}
          {estado === 'erro' && (
            <>
              <p
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: '13px',
                  color: 'var(--coral)',
                  marginBottom: '20px',
                  lineHeight: 1.6,
                }}
              >
                ✗ {erroMsg}
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleFecharDefinitivo}
                  style={btnSecundario}
                >
                  {t('plantao.close.skipPassage')}
                </button>
                <button
                  type="button"
                  onClick={handleGerarPassagem}
                  style={btnPrimario}
                >
                  {t('plantao.close.tryAgain')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  },
);

PlantaoCloseModal.displayName = 'PlantaoCloseModal';
